import * as React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Boxes,
  Globe,
  HelpCircle,
  Layers,
  Mail,
  Plus,
  Repeat,
  Server,
  Sparkles
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { OnboardingProgress } from "@/components/onboarding/OnboardingProgress";
import { OptionCard } from "@/components/onboarding/OptionCard";
import { RecommendationCard } from "@/components/onboarding/RecommendationCard";

/**
 * Anonymous 5-step wizard. Lives on /onboarding (public). State is
 * client-only — refresh loses progress. Recommendation logic mirrors the
 * Node API's heuristic but extended to factor in services_needed.
 *
 * Step shapes:
 *   1. Domain situation     → have | need | transfer | multiple
 *   2. Services needed      → email | hosting | portfolio | both
 *   3. Uses Promonu / GHL   → yes  | no | not_sure
 *   4. Current registrar    → godaddy | namecheap | shopify | cloudflare | other | hosterdash
 *   5. Mailbox count        → integer 1..1000
 *
 * Final screen renders RecommendationCard, which routes to either
 * /api/checkout/quick (standard plans) or /api/checkout/portfolio-consultation
 * (Portfolio Custom). Both flows already exist from Phase 5.
 */

type DomainSituation = "have" | "need" | "transfer" | "multiple";
type ServicesNeeded  = "email" | "hosting" | "portfolio" | "both";
type UsesPromonu     = "yes" | "no" | "not_sure";
type Registrar       = "godaddy" | "namecheap" | "shopify" | "cloudflare" | "other" | "hosterdash";

interface WizardState {
  domainSituation: DomainSituation | null;
  servicesNeeded:  ServicesNeeded  | null;
  usesPromonu:     UsesPromonu     | null;
  registrar:       Registrar       | null;
  mailboxes:       number;
}

const STEP_LABELS = [
  "Domain",
  "Services",
  "Tools",
  "Registrar",
  "Mailboxes",
  "Recommendation"
];
const TOTAL_STEPS = STEP_LABELS.length;

export function OnboardingPage() {
  const [step, setStep] = React.useState(0);
  const [state, setState] = React.useState<WizardState>({
    domainSituation: null,
    servicesNeeded:  null,
    usesPromonu:     null,
    registrar:       null,
    mailboxes:       5
  });

  const canContinue = isStepValid(step, state);
  const isFinalStep = step === TOTAL_STEPS - 1;

  function next() { if (canContinue && step < TOTAL_STEPS - 1) setStep(s => s + 1); }
  function back() { if (step > 0) setStep(s => s - 1); }

  // ── Recommendation derivation (memoized — runs on every state change
  //    but cheap; surfaces inside RecommendationCard via props).
  const { slug, why } = React.useMemo(() => recommend(state), [state]);

  return (
    <div className="brand-gradient-bg brand-radial-halo relative min-h-[100dvh] overflow-hidden">
      <TopBar />

      <main className="relative z-10 mx-auto w-full max-w-3xl px-4 pb-20 pt-8 sm:px-6 sm:pt-12">
        <Header />

        <div className="mt-8 sm:mt-10">
          <OnboardingProgress current={step} total={TOTAL_STEPS} labels={STEP_LABELS} />
        </div>

        <div className="mt-8 sm:mt-10">
          {step === 0 && <StepDomain state={state} setState={setState} />}
          {step === 1 && <StepServices state={state} setState={setState} />}
          {step === 2 && <StepPromonu state={state} setState={setState} />}
          {step === 3 && <StepRegistrar state={state} setState={setState} />}
          {step === 4 && <StepMailboxes state={state} setState={setState} />}
          {step === 5 && (
            <RecommendationCard recommendedSlug={slug} cycle="annually" why={why} />
          )}
        </div>

        {!isFinalStep && (
          <Navigation
            step={step}
            canContinue={canContinue}
            onBack={back}
            onNext={next}
          />
        )}

        {isFinalStep && (
          <div className="mt-6 flex justify-center">
            <Button variant="ghost" onClick={() => setStep(0)}>
              <Repeat className="h-4 w-4" />
              Start over
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}

// ── Sub-pages (inline so the wizard state isn't prop-drilled deep) ─
function StepDomain({ state, setState }: StepProps) {
  return (
    <StepFrame title="What's your domain situation?" sub="So we know what kind of setup to recommend.">
      <div className="grid gap-3 sm:grid-cols-2">
        <OptionCard
          icon={<Globe className="h-5 w-5" />}
          title="I already have a domain"
          description="Registered somewhere else — pointing it here."
          selected={state.domainSituation === "have"}
          onClick={() => setState(s => ({ ...s, domainSituation: "have" }))}
        />
        <OptionCard
          icon={<Plus className="h-5 w-5" />}
          title="I need a new domain"
          description="We'll help you register one."
          selected={state.domainSituation === "need"}
          onClick={() => setState(s => ({ ...s, domainSituation: "need" }))}
        />
        <OptionCard
          icon={<Repeat className="h-5 w-5" />}
          title="I want to transfer mine"
          description="Move your domain into HosterDash."
          selected={state.domainSituation === "transfer"}
          onClick={() => setState(s => ({ ...s, domainSituation: "transfer" }))}
        />
        <OptionCard
          icon={<Layers className="h-5 w-5" />}
          title="I have multiple domains"
          description="Portfolio-style — managing many at once."
          selected={state.domainSituation === "multiple"}
          onClick={() => setState(s => ({ ...s, domainSituation: "multiple" }))}
          hint="Portfolio plan likely"
        />
      </div>
    </StepFrame>
  );
}

function StepServices({ state, setState }: StepProps) {
  return (
    <StepFrame title="What services do you need?" sub="Pick the closest fit — you can always add more later.">
      <div className="grid gap-3 sm:grid-cols-2">
        <OptionCard
          icon={<Mail className="h-5 w-5" />}
          title="Professional email"
          description="Mailboxes + bulk email deliverability."
          selected={state.servicesNeeded === "email"}
          onClick={() => setState(s => ({ ...s, servicesNeeded: "email" }))}
        />
        <OptionCard
          icon={<Server className="h-5 w-5" />}
          title="Website hosting + email"
          description="Full stack for one brand."
          selected={state.servicesNeeded === "hosting"}
          onClick={() => setState(s => ({ ...s, servicesNeeded: "hosting" }))}
        />
        <OptionCard
          icon={<Layers className="h-5 w-5" />}
          title="Multi-domain portfolio"
          description="Many domains, one account, shared pool."
          selected={state.servicesNeeded === "portfolio"}
          onClick={() => setState(s => ({ ...s, servicesNeeded: "portfolio" }))}
        />
        <OptionCard
          icon={<Boxes className="h-5 w-5" />}
          title="All of the above"
          description="Hosting + email + room to scale."
          selected={state.servicesNeeded === "both"}
          onClick={() => setState(s => ({ ...s, servicesNeeded: "both" }))}
        />
      </div>
    </StepFrame>
  );
}

function StepPromonu({ state, setState }: StepProps) {
  return (
    <StepFrame
      title="Do you use Promonu or GoHighLevel?"
      sub="Our deliverability stack is tuned for these — affects which plan fits best."
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <OptionCard
          title="Yes"
          description="Currently using one (or both)."
          selected={state.usesPromonu === "yes"}
          onClick={() => setState(s => ({ ...s, usesPromonu: "yes" }))}
        />
        <OptionCard
          title="No"
          description="I run my own marketing stack."
          selected={state.usesPromonu === "no"}
          onClick={() => setState(s => ({ ...s, usesPromonu: "no" }))}
        />
        <OptionCard
          title="Not sure"
          description="Tell me what these are first."
          selected={state.usesPromonu === "not_sure"}
          onClick={() => setState(s => ({ ...s, usesPromonu: "not_sure" }))}
        />
      </div>
    </StepFrame>
  );
}

function StepRegistrar({ state, setState }: StepProps) {
  const options: Array<{ value: Registrar; label: string }> = [
    { value: "godaddy",    label: "GoDaddy"       },
    { value: "namecheap",  label: "Namecheap"     },
    { value: "shopify",    label: "Shopify"       },
    { value: "cloudflare", label: "Cloudflare"    },
    { value: "hosterdash", label: "HosterDash"    },
    { value: "other",      label: "Other"         }
  ];
  return (
    <StepFrame
      title="Where is your domain registered?"
      sub="Helps us prep the right DNS instructions for you."
    >
      <div className="grid gap-3 sm:grid-cols-3">
        {options.map(opt => (
          <OptionCard
            key={opt.value}
            title={opt.label}
            selected={state.registrar === opt.value}
            onClick={() => setState(s => ({ ...s, registrar: opt.value }))}
          />
        ))}
      </div>
    </StepFrame>
  );
}

function StepMailboxes({ state, setState }: StepProps) {
  return (
    <StepFrame
      title="How many mailboxes do you need?"
      sub="An honest guess is fine — you can scale up later from any plan."
    >
      <div className="space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
          <div className="flex-1">
            <Label htmlFor="mailboxes">Mailboxes</Label>
            <Input
              id="mailboxes"
              type="number"
              inputMode="numeric"
              min={1}
              max={1000}
              value={state.mailboxes}
              onChange={e => {
                const n = Number(e.target.value);
                setState(s => ({ ...s, mailboxes: Number.isFinite(n) ? Math.max(1, Math.min(1000, n)) : 1 }));
              }}
              className="mt-1.5 text-lg"
            />
          </div>
          <div className="flex gap-2">
            {[1, 5, 10, 25, 50].map(n => (
              <Button
                key={n}
                type="button"
                variant={state.mailboxes === n ? "default" : "outline"}
                size="sm"
                onClick={() => setState(s => ({ ...s, mailboxes: n }))}
              >
                {n}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex items-start gap-3 rounded-lg border border-brand-border bg-brand-tint p-3 text-xs text-foreground sm:text-sm">
          <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-brand-accentDark" />
          <p>
            Includes the catch-all + shared inboxes you'll create. Aliases and forwarders
            don't count.
          </p>
        </div>
      </div>
    </StepFrame>
  );
}

// ── Shared layout pieces ────────────────────────────────────
function StepFrame({ title, sub, children }: { title: string; sub: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-border bg-background p-5 shadow-brand-md sm:p-7">
      <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground sm:text-base">{sub}</p>
      <div className="mt-5 sm:mt-6">{children}</div>
    </section>
  );
}

function Navigation({
  step,
  canContinue,
  onBack,
  onNext
}: {
  step: number;
  canContinue: boolean;
  onBack: () => void;
  onNext: () => void;
}) {
  return (
    <div className="mt-6 flex flex-col-reverse items-stretch gap-3 sm:mt-8 sm:flex-row sm:items-center sm:justify-between">
      <Button variant="outline" onClick={onBack} disabled={step === 0}>
        <ArrowLeft className="h-4 w-4" />
        Back
      </Button>
      <Button onClick={onNext} disabled={!canContinue} size="lg" className="sm:w-auto">
        {step === TOTAL_STEPS - 2 ? "See my recommendation" : "Continue"}
        <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

function Header() {
  return (
    <div className="text-center">
      <span className="brand-pill-recommended">
        <Sparkles className="h-3 w-3" />
        Quick start
      </span>
      <h1 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
        Find the right plan in 30 seconds
      </h1>
      <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground sm:text-base">
        Answer a few questions — we'll match you to the plan that fits.
      </p>
    </div>
  );
}

function TopBar() {
  return (
    <header className="relative z-20 mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-4 sm:px-6 sm:py-5">
      <Link to="/" className="flex items-center">
        <BrandLogo className="h-8 w-auto" />
      </Link>
      <Link
        to="/plans"
        className="rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
      >
        Skip — see all plans
      </Link>
    </header>
  );
}

// ── Step validation ────────────────────────────────────────
function isStepValid(step: number, s: WizardState): boolean {
  switch (step) {
    case 0: return s.domainSituation !== null;
    case 1: return s.servicesNeeded  !== null;
    case 2: return s.usesPromonu     !== null;
    case 3: return s.registrar       !== null;
    case 4: return s.mailboxes >= 1 && s.mailboxes <= 1000;
    default: return true;
  }
}

// ── Recommendation engine ──────────────────────────────────
// Decision tree intentionally simple — easier to tune from sales feedback
// than a fancy scoring model. Returns plan slug + a one-line "why".
interface Recommendation { slug: string; why: string; }

function recommend(s: WizardState): Recommendation {
  // Portfolio path — multiple domains OR explicitly chose portfolio.
  const portfolio = s.domainSituation === "multiple" || s.servicesNeeded === "portfolio";
  if (portfolio) {
    if (s.mailboxes > 25) {
      return {
        slug: "portfolio-growth",
        why: `${s.mailboxes} mailboxes across multiple domains needs the bigger pool that Portfolio Growth carves out.`
      };
    }
    if (s.mailboxes > 75) {
      return {
        slug: "portfolio-custom",
        why: "At this scale a custom-quoted Portfolio plan is more cost-effective — let's talk."
      };
    }
    return {
      slug: "portfolio-starter",
      why: "Portfolio Starter handles up to 5 domains with a shared pool — fits multi-brand setups out of the box."
    };
  }

  // Hosting path — "hosting" or "both".
  const wantsHosting = s.servicesNeeded === "hosting" || s.servicesNeeded === "both";
  if (wantsHosting) {
    if (s.mailboxes > 25) {
      return {
        slug: "hosting-pro",
        why: `Hosting Pro covers ${s.mailboxes} mailboxes plus the storage your site will need as it grows.`
      };
    }
    if (s.mailboxes > 10) {
      return {
        slug: "hosting-growth",
        why: `Hosting Growth includes ${10} mailboxes and 10k bulk emails — comfortable for ${s.mailboxes}-mailbox teams.`
      };
    }
    return {
      slug: "hosting-starter",
      why: "Hosting Starter pairs your website with email — perfect for getting up and running."
    };
  }

  // Email-only path.
  if (s.mailboxes > 25) {
    return {
      slug: "email-pro",
      why: `Email Pro handles up to 25 mailboxes and 15k bulk emails — fits ${s.mailboxes} mailboxes comfortably.`
    };
  }
  if (s.mailboxes > 10) {
    return {
      slug: "email-growth",
      why: `Email Growth's 10-mailbox pool + 10k bulk emails matches ${s.mailboxes} mailboxes nicely.`
    };
  }
  return {
    slug: "email-starter",
    why: `Email Starter is the right size for ${s.mailboxes} mailbox${s.mailboxes === 1 ? "" : "es"} and 5k bulk emails / month.`
  };
}

interface StepProps {
  state: WizardState;
  setState: React.Dispatch<React.SetStateAction<WizardState>>;
}
